import { TimeElapsed } from "@nova/component-time-elapsed"
import { dateFromNumber } from "@nova/serializer/src/client/util"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { useJobProgress } from "panels/content/side-panel/useJobProgress"
import { SidePanelProgressBar } from "@nova/component-side-panel-tab-container"
import { ButtonPrimary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { InspectorPanelMetadata } from "features/map/inspector-panel/FlightPanelMetadata"
import { GenericUpload, JobStatusType, OrthoProcessingMode, SidePanelLayer } from "@nova/constants"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { DeleteFlight } from "./components/DeleteFlight"
import { setActiveFeatureId, setActiveLayerId } from "../slice.new"

const getRapidModeTimeout = (progress: number): number => {
    if (progress < 30) return 3 * 60 * 1000
    if (progress < 65) return 5 * 60 * 1000
    return 10 * 60 * 1000
}

// Thresholds mirror stitch-server rapid-mode progress phases
const getRapidModePhase = (progress: number): string => {
    if (progress < 30) return "Aligning images..."
    if (progress < 65) return "Generating orthomosaic..."
    return "Rendering..."
}

const RapidModeStatusChip = ({ progress }: { progress: number }) => (
    <div className="flex flex-col gap-1.5 mt-4">
        <small className="mono text-gray-400">{getRapidModePhase(progress)}</small>
        {progress > 65 && <small className="mono text-gray-400">Preview available on map</small>}
    </div>
)

export const GenericProcessingFiles = () => {
    const { job, jobProgressType, flightId, uploads, selectFlightById, selectUploadSize } =
        useUploadFlowContext()
    const dispatch = useAppDispatch()
    const flight = useAppSelector(selectFlightById(flightId))
    const progress = useJobProgress({ job, type: jobProgressType! }) || 1
    const uploadSize = useAppSelector(selectUploadSize(flightId))

    const isRapidMode =
        !!job && "processingMode" in job && job.processingMode === OrthoProcessingMode.Rapid

    let jobFailed = job?.status === JobStatusType.FAILED
    const jobComplete = job?.status === JobStatusType.COMPLETED

    const processingTimeout =
        isRapidMode && job?.status !== JobStatusType.COMPLETED
            ? getRapidModeTimeout(progress)
            : 24 * 60 * 60 * 1000

    const jobStartTime = job?.createdAt

    if (jobStartTime) {
        const elapsed = new Date().getTime() - dateFromNumber(job.createdAt).getTime()
        if (
            job?.status === JobStatusType.STARTED ||
            (job?.status !== JobStatusType.COMPLETED && elapsed >= processingTimeout)
        ) {
            jobFailed = true
        }
    }

    const onDone = () => {
        dispatch(setActiveLayerId({ id: undefined }))
        dispatch(setActiveFeatureId({ feature: undefined }))
    }
    return (
        <div className="flex flex-col w-full">
            <div className="border-b border-gray-700 mt-2" />
            {!!progress && job?.createdAt && !jobFailed && !jobComplete && (
                <div className="mb-4">
                    <div className="flex items-center justify-between mt-4 mb-2">
                        <div className="flex items-center gap-1">
                            <h5 className="text-gray-0">Processing:</h5>
                            <h5 className="text-gray-100">
                                {Math.max(1, progress).toString() + "%"}
                            </h5>
                        </div>
                        <TimeElapsed
                            start={dateFromNumber(job.createdAt || 0)}
                            className="!text-gray-100"
                        />
                    </div>
                    <SidePanelProgressBar progress={progress} total={100} colorHex={"#27BBFF"} />
                    {isRapidMode && <RapidModeStatusChip progress={progress} />}
                </div>
            )}
            {jobComplete && (
                <div className="mb-4">
                    <div className="flex items-center mt-4 mb-4 gap-1">
                        <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />
                        <h5 className="text-gray-0">Process Complete</h5>
                    </div>
                    <ButtonPrimary title="Done" className="mt-2 w-full" onClick={onDone} />
                </div>
            )}
            {jobFailed && (
                <div className="mb-4">
                    <div className="flex items-center mt-4 mb-4 gap-1">
                        <Icons.SmallIcon Icon={Icons.Error} className="text-red-500" />
                        <h5 className="text-gray-0">Process Failed</h5>
                    </div>
                    <ButtonPrimary title="Done" className="mt-2 w-full" onClick={onDone} />
                </div>
            )}
            <InspectorPanelMetadata
                entity={flight as SidePanelLayer}
                uploads={uploads as GenericUpload[]}
                uploadSize={uploadSize}
                completedUploadCount={uploads.length}
                hideCreator
                hideFileCount
                className=""
            />
            <div className="border-b border-gray-700 mb-2" />
            <DeleteFlight />
        </div>
    )
}
